import { useState } from "react";
import { useRecordContext, useDataProvider, useNotify, useRefresh } from "ra-core";
import { Button } from "@/components/ui/button";
import type { CrmDataProvider, LoadWorkflowAction, LoadWorkflowResponse } from "../providers/supabase/dataProvider";
import type { Sale } from "../types";
import { LoadWorkflowModal } from "./LoadWorkflowModal";

export type WorkflowButtonDef = {
  action: LoadWorkflowAction;
  label: string;
  variant?: "default" | "outline" | "destructive";
  from: string[];
  roles: Sale["roles"];
  requiresReason?: boolean;
  confirmMessage?: string;
};

const WORKFLOW_BUTTONS: WorkflowButtonDef[] = [
  {
    action: "approve_so",
    label: "SO Approved",
    from: ["pending_so_approval"],
    roles: ["sales", "workflow_manager"],
  },
  {
    action: "closed_lost",
    label: "Closed Lost",
    variant: "destructive",
    from: [
      "pending_so_approval",
      "closed_won_pca",
      "pca_hold",
      "closed_won_ppoa",
      "lg_booking",
      "lg_booked",
      "invoiced",
    ],
    roles: ["sales", "purchasing", "logistics", "accounting", "ar", "ap", "workflow_manager"],
    confirmMessage: "Are you sure you want to set this Load to Closed Lost?",
  },
  {
    action: "sos",
    label: "S.O.S.",
    variant: "destructive",
    from: [
      "pending_so_approval",
      "closed_won_pca",
      "pca_hold",
      "closed_won_ppoa",
      "lg_booking",
      "lg_booked",
      "lg_picked_up",
    ],
    roles: ["sales", "purchasing", "logistics", "accounting", "ar", "ap", "workflow_manager"],
    confirmMessage: "Are you sure you want to set this Load to S.O.S. Status?",
  },
  {
    action: "approve_load",
    label: "Approve Load",
    from: ["closed_won_pca", "pca_hold"],
    roles: ["accounting", "ar", "workflow_manager"],
  },
  {
    action: "pca_hold",
    label: "PCA Hold",
    variant: "outline",
    from: ["closed_won_pca", "closed_won_ppoa"],
    roles: ["accounting", "ar", "workflow_manager"],
    requiresReason: true,
    confirmMessage: "Are you sure you want to put this Load on PCA Hold?",
  },
  {
    action: "po_approved",
    label: "PO Approved",
    from: ["closed_won_ppoa"],
    roles: ["purchasing", "workflow_manager"],
  },
  {
    action: "bookings_confirmed",
    label: "Bookings Confirmed",
    from: ["lg_booking"],
    roles: ["logistics", "workflow_manager"],
  },
  {
    action: "lg_hold",
    label: "LG Hold",
    variant: "outline",
    from: ["lg_booking", "lg_booked", "lg_picked_up"],
    roles: ["logistics", "workflow_manager"],
    confirmMessage: "Are you sure you want to set this Load to LG Hold?",
  },
  {
    action: "pickup_confirmed",
    label: "Pickup Confirmed",
    from: ["lg_booked"],
    roles: ["logistics", "workflow_manager"],
    confirmMessage: "Are you sure the Load's pickup was confirmed?",
  },
  {
    action: "ready_to_invoice",
    label: "Ready to Invoice",
    variant: "outline",
    from: ["lg_picked_up"],
    roles: ["logistics", "workflow_manager"],
  },
  {
    action: "invoices_entered",
    label: "Invoices Entered",
    from: ["lg_picked_up"],
    roles: ["logistics", "workflow_manager"],
  },
  {
    action: "remove_lg_hold",
    label: "Remove LG Hold",
    variant: "outline",
    from: ["logistics_hold"],
    roles: ["logistics", "workflow_manager"],
    confirmMessage: "Are you sure you want to remove LG Hold?",
  },
  {
    action: "audit_complete",
    label: "Audit Complete",
    from: ["invoiced"],
    roles: ["accounting", "ar", "workflow_manager"],
    confirmMessage: "Are you sure the Load's audit is complete?",
  },
  {
    action: "paid",
    label: "Paid",
    variant: "outline",
    from: ["invoiced", "completed", "lg_picked_up", "lg_booked"],
    roles: ["ap", "accounting", "workflow_manager"],
  },
  {
    action: "remove_paid",
    label: "Remove Paid",
    variant: "outline",
    from: ["invoiced", "completed", "lg_picked_up", "lg_booked"],
    roles: ["ap", "accounting", "workflow_manager"],
  },
  {
    action: "resolve_sos",
    label: "Resolve S.O.S.",
    variant: "outline",
    from: ["sos"],
    roles: ["accounting", "ar", "workflow_manager"],
    confirmMessage: "Remove SOS?",
  },
];

export const LoadWorkflowButtons = ({ sale }: { sale?: Sale }) => {
  const record = useRecordContext();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [modalState, setModalState] = useState<{
    open: boolean;
    action: LoadWorkflowAction | null;
    label: string;
    requiresReason: boolean;
    response: LoadWorkflowResponse | null;
  }>({
    open: false,
    action: null,
    label: "",
    requiresReason: false,
    response: null,
  });
  const [loading, setLoading] = useState(false);

  if (!record || !sale) return null;

  const userRoles = new Set(sale.roles ?? []);
  const isAdmin = sale.administrator;

  const visibleButtons = WORKFLOW_BUTTONS.filter((button) => {
    if (button.from.length > 0 && !button.from.includes(record.status)) {
      return false;
    }
    if (isAdmin) return true;
    return button.roles.some((role) => userRoles.has(role));
  });

  if (visibleButtons.length === 0) return null;

  const handleClick = async (button: WorkflowButtonDef) => {
    setLoading(true);
    try {
      const response = await dataProvider.loadWorkflow(record.id, button.action, {
        confirm: false,
      });
      setModalState({
        open: true,
        action: button.action,
        label: button.label,
        requiresReason: button.requiresReason ?? false,
        response,
      });
    } catch (err: any) {
      notify(err?.message || "Failed to validate workflow action", {
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (reason?: string) => {
    if (!modalState.action) return;
    setLoading(true);
    try {
      const response = await dataProvider.loadWorkflow(
        record.id,
        modalState.action,
        { confirm: true, reason },
      );
      if (response.executed) {
        notify(`${modalState.label} completed`, { type: "success" });
        refresh();
      } else {
        notify(response.error || "Workflow action could not be completed", {
          type: "error",
        });
      }
      setModalState({ ...modalState, open: false });
    } catch (err: any) {
      notify(err?.message || "Failed to execute workflow action", {
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {visibleButtons.map((button) => (
          <Button
            key={button.action}
            variant={button.variant ?? "default"}
            size="sm"
            onClick={() => handleClick(button)}
            disabled={loading}
          >
            {button.label}
          </Button>
        ))}
      </div>

      <LoadWorkflowModal
        open={modalState.open}
        onOpenChange={(open) => setModalState({ ...modalState, open })}
        title={modalState.label}
        response={modalState.response}
        requiresReason={modalState.requiresReason}
        onConfirm={handleConfirm}
        loading={loading}
      />
    </>
  );
};
